/**
 * Purchase-order composer — shared between `/procurement/purchase-orders/new` and
 * `/procurement/purchase-orders/$poId/edit`.
 *
 * This component is a faithful React reconstruction of the design prototype
 * `prototype/purchases/purchase-order-new.html`. The layout maps section by
 * section to the prototype:
 *   - composer-head : back arrow + "New purchase order" title + draft pill +
 *                     actions (`.composer-head`).
 *   - section 1     : Vendor — `.vendor-card` + `.vendor-history` +
 *                     `.field-grid cols-2` (vendor select + INR currency).
 *   - section 2     : Order details — `.field-grid cols-3` (PO number · order
 *                     date · expected delivery).
 *   - section 3     : Line items — `.items-table` + in-card totals strip.
 *   - section 4     : Notes & terms — `.field-grid cols-2` (delivery
 *                     instructions + internal note).
 *   - section 5     : Approval routing — the `.ai-hint` "no payment is
 *                     triggered by the PO" callout.
 *   - right rail    : live document preview (`.composer-preview`).
 *   - composer-foot : ready pill + status line + Cancel / Save draft / Save & send.
 *
 * Mode is set by the optional `loadPoId` prop:
 *   - `undefined` → new mode. Empty form. Save = `purchaseOrders.create`.
 *   - `<uuid>`    → edit mode. Hydrate from `purchaseOrders.get`. Save = `purchaseOrders.update`.
 *
 * Vendor is locked on edit (the wire contract's `updateInput` doesn't accept
 * `vendorId`). Only drafts are editable; anything sent/confirmed renders a
 * locked banner. Paise totals are computed client-side for preview and sent
 * as STRINGIFIED integer paise; line inputs are forwarded as the operator
 * strings the domain re-computes.
 *
 * @see prototype/purchases/purchase-order-new.html — V1.1 composer baseline
 * @see packages/api-contract/src/procedures/purchase-orders.ts
 */
import { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, Loader2, Lock, Send, ShieldCheck } from "lucide-react";
import { Alert, Button, Form, Input, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { meQueryOptions } from "../../../lib/session.js";
import { paiseToInr } from "../../../lib/format.js";
import { computeTotals, hasValidPoLine } from "../lib/po-math.js";
import { poStatusLabel } from "../lib/status.js";
import {
  EMPTY_PO_FORM,
  emptyPoLine,
  poFormSchema,
  type PoFormValues,
  type PoLineItem,
} from "../lib/form-schema.js";
import { PoLineItems } from "./po-line-items.js";
import { PoField, PoFieldGrid, PoSectionCard } from "./po-section-card.js";
import { PoVendorSection, type PoVendorOption } from "./po-vendor-section.js";
import { PoPreview } from "./po-preview.js";

export interface PoComposerPageProps {
  /** When set, hydrate from `purchaseOrders.get` and save via `update`. */
  loadPoId?: string;
}

export function PoComposerPage({ loadPoId }: PoComposerPageProps = {}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const isEditMode = !!loadPoId;

  const me = useQuery(meQueryOptions);
  const company = me.data?.company ?? null;

  const vendors = trpc.vendors.list.useQuery({ limit: 100 });
  const loadedPo = trpc.purchaseOrders.get.useQuery(
    { id: loadPoId ?? "" },
    { enabled: isEditMode },
  );

  const create = trpc.purchaseOrders.create.useMutation({
    onSuccess: async () => {
      await utils.purchaseOrders.list.invalidate();
    },
  });
  const update = trpc.purchaseOrders.update.useMutation({
    onSuccess: async () => {
      if (loadPoId) {
        await Promise.all([
          utils.purchaseOrders.get.invalidate({ id: loadPoId }),
          utils.purchaseOrders.list.invalidate(),
        ]);
      }
    },
  });
  const send = trpc.purchaseOrders.send.useMutation({
    onSuccess: async () => {
      if (loadPoId) {
        await Promise.all([
          utils.purchaseOrders.get.invalidate({ id: loadPoId }),
          utils.purchaseOrders.list.invalidate(),
        ]);
      }
    },
  });

  const form = useForm<PoFormValues>({
    resolver: zodResolver(poFormSchema),
    defaultValues: EMPTY_PO_FORM,
    mode: "onBlur",
  });

  // Hydrate the form once when the PO row lands (edit mode).
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!isEditMode || !loadedPo.data) return;
    hydratedRef.current = true;
    const po = loadedPo.data;
    form.reset({
      vendorId: po.vendorId,
      orderDate: po.orderDate,
      expectedDate: po.expectedDate ?? "",
      vendorNote: po.vendorNote ?? "",
      internalNote: po.internalNote ?? "",
      lineItems:
        po.lineItems.length > 0 ? po.lineItems.map(lineFromPersisted) : [emptyPoLine(1)],
    });
  }, [isEditMode, loadedPo.data, form]);

  const values = useWatch({ control: form.control }) as PoFormValues;
  const totals = useMemo(() => computeTotals(values.lineItems ?? []), [values.lineItems]);
  const errors = form.formState.errors;

  const vendorOptions = useMemo<PoVendorOption[]>(
    () =>
      (vendors.data?.items ?? []).map((v) => ({
        id: v.id,
        name: v.name,
        gstin: v.gstin,
        state: v.state,
        city: v.city,
        contactName: v.contactName,
        email: "email" in v ? ((v as { email: string | null }).email ?? null) : null,
        phone: v.phone,
      })),
    [vendors.data?.items],
  );
  const selectedVendor = vendorOptions.find((v) => v.id === values.vendorId) ?? null;

  // Edit-mode lifecycle gate — only drafts are editable.
  const isLoadedAndUneditable =
    isEditMode && loadedPo.data !== undefined && loadedPo.data.status !== "draft";

  /** Field patcher — `as never` at the RHF boundary mirrors the bill composer. */
  const patch = <K extends keyof PoFormValues>(field: K, value: PoFormValues[K]) => {
    form.setValue(field, value as never, { shouldDirty: true, shouldTouch: true });
    form.clearErrors(field);
  };

  const onVendorChange = (vendorId: string) => {
    if (isEditMode) return; // vendor locked on update (not on the wire contract)
    patch("vendorId", vendorId);
  };

  /** Build the create/update payload from the validated form values. */
  function buildPayload(parsed: PoFormValues) {
    const finalTotals = computeTotals(parsed.lineItems);
    const lineItems = parsed.lineItems.filter(hasValidPoLine).map((line) => ({
      description: line.description.trim(),
      hsn: line.hsn,
      qty: line.qty,
      unit: line.unit,
      rate: line.rate,
      discountRate: line.discountRate || "0",
      taxRate: line.taxRate || "18",
    }));
    return {
      lineItems,
      subtotalPaise: finalTotals.subtotalPaise.toString(),
      taxPaise: finalTotals.taxPaise.toString(),
      totalPaise: finalTotals.totalPaise.toString(),
      finalTotals,
    };
  }

  function validateLines(parsed: PoFormValues): boolean {
    const withContent = parsed.lineItems.filter(hasValidPoLine);
    if (withContent.length === 0) {
      form.setError("lineItems", {
        type: "manual",
        message: "Add at least one line with a description, quantity, and rate.",
      });
      return false;
    }
    if (computeTotals(parsed.lineItems).totalPaise <= 0n) {
      form.setError("lineItems", {
        type: "manual",
        message: "Order total must be greater than zero.",
      });
      return false;
    }
    return true;
  }

  /** Save draft (create on new, update on edit). */
  const saveDraft = form.handleSubmit(async (parsed) => {
    if (!validateLines(parsed)) return;
    const { lineItems, subtotalPaise, taxPaise, totalPaise } = buildPayload(parsed);
    try {
      if (isEditMode && loadPoId) {
        await update.mutateAsync({
          id: loadPoId,
          orderDate: parsed.orderDate,
          expectedDate: parsed.expectedDate || null,
          subtotalPaise,
          taxPaise,
          totalPaise,
          vendorNote: parsed.vendorNote || null,
          internalNote: parsed.internalNote || null,
          lineItems,
        });
        toast.success("Purchase order saved", {
          description: "Returning to the order detail.",
        });
        void navigate({ to: "/procurement/purchase-orders/$poId", params: { poId: loadPoId } });
        return;
      }
      const result = await create.mutateAsync({
        vendorId: parsed.vendorId,
        orderDate: parsed.orderDate,
        expectedDate: parsed.expectedDate || null,
        subtotalPaise,
        taxPaise,
        totalPaise,
        vendorNote: parsed.vendorNote || null,
        internalNote: parsed.internalNote || null,
        lineItems,
      });
      toast.success("Saved as draft", {
        description: "Send the order to the vendor when you're ready — it's numbered on send.",
      });
      void navigate({ to: "/procurement/purchase-orders/$poId", params: { poId: result.id } });
    } catch (err) {
      toast.error("Couldn't save purchase order", {
        description: err instanceof Error ? err.message : "Try again in a moment.",
      });
    }
  });

  /**
   * Save + send — persist the latest header/lines, then call `send` to
   * number and dispatch the order. New-mode creates first, then sends the
   * freshly-minted id.
   */
  const saveAndSend = form.handleSubmit(async (parsed) => {
    if (!validateLines(parsed)) return;
    const { lineItems, subtotalPaise, taxPaise, totalPaise } = buildPayload(parsed);
    try {
      let poId = loadPoId ?? null;
      if (isEditMode && loadPoId) {
        await update.mutateAsync({
          id: loadPoId,
          orderDate: parsed.orderDate,
          expectedDate: parsed.expectedDate || null,
          subtotalPaise,
          taxPaise,
          totalPaise,
          vendorNote: parsed.vendorNote || null,
          internalNote: parsed.internalNote || null,
          lineItems,
        });
      } else {
        const result = await create.mutateAsync({
          vendorId: parsed.vendorId,
          orderDate: parsed.orderDate,
          expectedDate: parsed.expectedDate || null,
          subtotalPaise,
          taxPaise,
          totalPaise,
          vendorNote: parsed.vendorNote || null,
          internalNote: parsed.internalNote || null,
          lineItems,
        });
        poId = result.id;
      }
      if (!poId) return;
      const sent = await send.mutateAsync({ id: poId });
      toast.success(`Purchase order ${sent.poNumber} sent`, {
        description: "The order is numbered and on its way to the vendor.",
      });
      void navigate({ to: "/procurement/purchase-orders/$poId", params: { poId } });
    } catch (err) {
      toast.error("Couldn't send purchase order", {
        description: err instanceof Error ? err.message : "Try again in a moment.",
      });
    }
  });

  const validLineCount = (values.lineItems ?? []).filter(hasValidPoLine).length;
  const ready =
    !!values.vendorId && !!values.orderDate && totals.totalPaise > 0n && !isLoadedAndUneditable;
  const pending = create.isPending || update.isPending || send.isPending;

  if (isEditMode && loadedPo.isLoading) {
    return <p className="p-6 text-sm text-muted">Loading purchase order…</p>;
  }
  if (isEditMode && loadedPo.error) {
    return (
      <Alert
        intent="bad"
        title="Couldn't load purchase order"
        description={loadedPo.error.message}
      />
    );
  }

  const backTo = isEditMode && loadPoId ? "/procurement/purchase-orders/$poId" : "/procurement/purchase-orders";
  const docNum = isEditMode ? (loadedPo.data?.poNumber ?? "Draft") : "Draft";
  const previewStatus = isEditMode ? (loadedPo.data?.status ?? "draft") : "draft";

  return (
    <Form {...form}>
      <form onSubmit={saveDraft} className="flex min-h-full flex-col bg-bg">
        {/* ── Composer head — sticky band (.composer-head) ─────────────── */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6">
          <Link
            to={backTo}
            {...(isEditMode && loadPoId ? { params: { poId: loadPoId } } : {})}
            aria-label={isEditMode ? "Back to purchase order" : "Back to purchase orders"}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 text-base font-bold leading-tight tracking-tight text-ink">
              {isEditMode ? "Edit purchase order" : "New purchase order"}
              <span className="rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
                {docNum}
              </span>
              {isLoadedAndUneditable ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-warn-50 px-2 py-0.5 text-xs font-medium text-warn">
                  <Lock className="h-3 w-3" />
                  Locked · {poStatusLabel(loadedPo.data!.status)}
                </span>
              ) : null}
            </h1>
            <p className="mt-0.5 text-xs text-muted">
              {isEditMode
                ? "Editing the order — vendor is locked; only the header and lines update."
                : "Commit to buy from a vendor — goods receipt and bill are created later when goods arrive."}
            </p>
          </div>
        </header>

        {/* ── Two-column body (.composer-body) ─────────────────────────── */}
        <main className="mx-auto grid w-full max-w-[1400px] flex-1 gap-5 px-6 pb-28 pt-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-4">
            {vendors.error ? (
              <Alert
                intent="bad"
                title="Couldn't load vendors"
                description={vendors.error.message}
              />
            ) : null}
            {create.error ? (
              <Alert intent="bad" title="Couldn't save order" description={create.error.message} />
            ) : null}
            {update.error ? (
              <Alert intent="bad" title="Couldn't update order" description={update.error.message} />
            ) : null}
            {send.error ? (
              <Alert intent="bad" title="Couldn't send order" description={send.error.message} />
            ) : null}
            {isLoadedAndUneditable ? (
              <Alert
                intent="warn"
                title="Purchase order is locked"
                description="Only draft orders can be edited. This order has already been sent."
              />
            ) : null}

            {/* 1 · Vendor */}
            <PoVendorSection
              vendors={vendorOptions}
              vendorId={values.vendorId || ""}
              loading={vendors.isLoading}
              locked={isEditMode}
              error={errors.vendorId?.message}
              onVendorChange={onVendorChange}
            />

            {/* 2 · Order details */}
            <PoSectionCard
              step="2"
              title="Order details"
              description="Dates the order is placed and expected. The PO number is assigned on send."
            >
              <PoFieldGrid cols={3}>
                <PoField id="po-number" label="PO number" mono help="Auto-assigned on send">
                  <Input
                    id="po-number"
                    value={docNum}
                    readOnly
                    disabled
                    className="h-9 font-mono text-sm"
                  />
                </PoField>
                <PoField
                  id="po-order-date"
                  label="Order date"
                  required
                  mono
                  error={errors.orderDate?.message}
                  help="Date you're placing the order"
                >
                  <Input
                    id="po-order-date"
                    type="date"
                    value={values.orderDate || ""}
                    onChange={(e) => patch("orderDate", e.currentTarget.value)}
                    disabled={isLoadedAndUneditable}
                    className="h-9 font-mono text-sm"
                  />
                </PoField>
                <PoField
                  id="po-expected-date"
                  label="Expected delivery"
                  mono
                  help="Agreed delivery date with vendor"
                >
                  <Input
                    id="po-expected-date"
                    type="date"
                    value={values.expectedDate || ""}
                    onChange={(e) => patch("expectedDate", e.currentTarget.value)}
                    disabled={isLoadedAndUneditable}
                    className="h-9 font-mono text-sm"
                  />
                </PoField>
              </PoFieldGrid>
            </PoSectionCard>

            {/* 3 · Line items */}
            <PoLineItems
              rows={values.lineItems ?? []}
              totals={totals}
              error={errors.lineItems?.message ?? errors.lineItems?.root?.message}
              disabled={pending || isLoadedAndUneditable}
              onRowsChange={(lineItems) => patch("lineItems", lineItems)}
            />

            {/* 4 · Notes & terms */}
            <PoSectionCard
              step="4"
              title="Notes & terms"
              description="Delivery instructions print on the order; the internal note is for your team only."
            >
              <PoFieldGrid cols={2}>
                <PoField id="po-vendor-note" label="Delivery instructions">
                  <textarea
                    id="po-vendor-note"
                    rows={4}
                    value={values.vendorNote || ""}
                    onChange={(e) => patch("vendorNote", e.currentTarget.value)}
                    placeholder="Packing, labelling, delivery slot…"
                    disabled={isLoadedAndUneditable}
                    className="min-h-[72px] w-full resize-y rounded-sm border border-line bg-paper px-3 py-2 text-sm leading-relaxed text-ink outline-none transition-colors placeholder:text-muted hover:border-line-2 focus-visible:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100 disabled:opacity-60"
                  />
                </PoField>
                <PoField id="po-internal-note" label="Internal note">
                  <textarea
                    id="po-internal-note"
                    rows={4}
                    value={values.internalNote || ""}
                    onChange={(e) => patch("internalNote", e.currentTarget.value)}
                    placeholder="For your team — never shared with the vendor."
                    disabled={isLoadedAndUneditable}
                    className="min-h-[72px] w-full resize-y rounded-sm border border-line bg-paper px-3 py-2 text-sm leading-relaxed text-ink outline-none transition-colors placeholder:text-muted hover:border-line-2 focus-visible:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100 disabled:opacity-60"
                  />
                </PoField>
              </PoFieldGrid>
            </PoSectionCard>

            {/* 5 · Approval routing — the prototype's "no payment" .ai-hint */}
            <PoSectionCard
              step="5"
              title="Approval routing"
              description="A purchase order commits to buy. It never moves money on its own."
            >
              <div className="flex items-start gap-2.5 rounded-sm border border-sky-100 border-l-[3px] border-l-sky-600 bg-sky-50 px-3 py-2.5 text-xs leading-relaxed text-ink-2">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-700" aria-hidden />
                <p className="min-w-0 flex-1">
                  <strong className="font-semibold text-ink">
                    No payment is triggered by the PO.
                  </strong>{" "}
                  This order commits to buy — a draft bill is created only after the goods receipt
                  is logged and matched. Payment follows the bill approval flow.
                </p>
              </div>
            </PoSectionCard>
          </div>

          {/* ── Right column · live document preview (.composer-preview) ── */}
          <PoPreview
            poNumber={docNum}
            status={previewStatus}
            vendor={selectedVendor}
            orderDate={values.orderDate || ""}
            expectedDate={values.expectedDate || ""}
            lineItems={values.lineItems ?? []}
            totals={totals}
            company={company}
          />
        </main>

        {/* ── Sticky footer (.composer-foot) ───────────────────────────── */}
        <footer className="sticky bottom-0 z-20 border-t border-line bg-paper px-6 py-3 shadow-card">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3">
            <span
              className={[
                "inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold",
                ready ? "bg-emerald-50 text-emerald-700" : "bg-warn-50 text-warn",
              ].join(" ")}
            >
              <Check size={13} aria-hidden />
              {ready ? "Ready to create" : "Vendor + a priced line needed"}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-muted">
              {validLineCount} line item{validLineCount === 1 ? "" : "s"} ·{" "}
              <strong className="font-mono text-ink">{paiseToInr(totals.totalPaise)}</strong> total
              {selectedVendor ? ` · PO will be sent to ${selectedVendor.name}` : ""}
            </span>
            <Button
              type="button"
              intent="ghost"
              onClick={() =>
                isEditMode && loadPoId
                  ? void navigate({ to: "/procurement/purchase-orders/$poId", params: { poId: loadPoId } })
                  : void navigate({ to: "/procurement/purchase-orders" })
              }
            >
              Cancel
            </Button>
            <Button
              type="button"
              intent="ghost"
              className="border border-line bg-paper"
              disabled={pending || isLoadedAndUneditable}
              onClick={() => void saveDraft()}
            >
              {pending ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              Save draft
            </Button>
            <Button type="button" disabled={pending || !ready} onClick={() => void saveAndSend()}>
              {pending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Save &amp; send
            </Button>
          </div>
        </footer>
      </form>
    </Form>
  );
}

/** Persisted line detail → editable form line (paise/bps/milli → operator strings). */
function lineFromPersisted(line: {
  id: string;
  description: string;
  hsn: string;
  qtyMilli: number;
  unit: string;
  ratePaise: string;
  discountBps: number;
  taxRateBps: number;
}): PoLineItem {
  return {
    id: line.id,
    description: line.description,
    hsn: line.hsn,
    qty: formatMilliAsDecimal(line.qtyMilli),
    unit: line.unit,
    rate: formatPaiseAsRupeeInput(BigInt(line.ratePaise)),
    discountRate: formatBpsAsPercent(line.discountBps),
    taxRate: formatBpsAsPercent(line.taxRateBps),
  };
}

function formatPaiseAsRupeeInput(paise: bigint): string {
  if (paise === 0n) return "0";
  const rupees = paise / 100n;
  const remainder = paise % 100n;
  if (remainder === 0n) return rupees.toString();
  return `${rupees}.${String(remainder).padStart(2, "0")}`;
}

function formatMilliAsDecimal(milli: number): string {
  const whole = Math.trunc(milli / 1000);
  const remainder = milli % 1000;
  if (remainder === 0) return String(whole);
  return `${whole}.${String(remainder).padStart(3, "0").replace(/0+$/, "")}`;
}

function formatBpsAsPercent(bps: number): string {
  const whole = Math.trunc(bps / 100);
  const remainder = bps % 100;
  if (remainder === 0) return String(whole);
  return `${whole}.${String(remainder).padStart(2, "0").replace(/0+$/, "")}`;
}
